import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { VesselRateCalculationService } from '../../../core/services/vessel-rate-calculation-service';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import { NumberFormatterService } from '../../../core/services/number.formatter.service';
import { PayoutDAO } from '../../../core/services/dao/payoutDAO';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { RateType } from 'anchor-shared/lib/model/enum/vessel-rate-types';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { getAddOnCost } from 'anchor-shared/lib/service/pure-functions/charter-pricing/charter-pricing-helper';
import { DefinedAddOnType } from 'anchor-shared/lib/model/entity/addOn';
import { RatePayee } from 'anchor-shared/lib/model/entity/vessel-rate';

@Component({
  selector: 'app-charter-price-detail-dialog',
  templateUrl: './charter-price-detail-dialog.component.html',
  styleUrls: ['./charter-price-detail-dialog.component.scss'],
})
export class CharterPriceDetailDialogComponent implements OnInit, OnDestroy {
  payouts: Payout[];
  vessel: Vessel;
  charter: Charter;
  durationType: 0 | 4 | 8;
  customNumberOfHours: number;
  viewType: string = 'all';
  headerTitle = {
    all: 'Price detail',
    buyflow: 'Base price breakdown',
    discount: 'Discount breakdown',
    promo: 'Promotion breakdown',
  };
  showDuration: boolean = false;

  filteredPayouts: Payout[];
  flattenedPayouts: Payout[];
  costPayouts: Payout[];
  baseRatePayouts: Payout[];
  currentPrice: number;
  discounts: any[];
  promo: any;
  services: any[] = [];
  discountTotal = 0;
  serviceTotal = 0;
  optionalAddOns = [];
  requiredAddOns = [];
  totalCost = 0;
  subs: any[] = [];
  optionalAddOnsTotalCost: number = 0;
  requiredAddOnsTotalCost: number = 0;
  optionalAddOnsTotalWithMarkUp: number = 0;
  requiredAddOnsTotalWithMarkUp: number = 0;

  constructor(
    public dialogRef: MatDialogRef<CharterPriceDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private calcService: VesselRateCalculationService,
    private numFormatter: NumberFormatterService,
    private payoutDAO: PayoutDAO,
  ) {
    if (
      this.data.charter &&
      this.data.charter.pricingMetaData &&
      this.data.charter.pricingMetaData.promoCode
    ) {
      this.promo = this.data.charter.pricingMetaData.promoCode;
    }
    this.durationType = data.durationType;
    this.customNumberOfHours = data.customNumberOfHours;
    this.vessel = data.vessel;
    if (data.discounts) {
      this.discounts = this.numFormatter.parseArrayFromJson(data.discounts);
    }
    if (data.services) {
      this.services = data.services;
    }
    this.initDiscountsAndServices();

    this.currentPrice = this.calcService.getBoatCaptainCrewTotal(
      this.vessel,
      this.durationType,
      this.customNumberOfHours,
    );
    this.charter = this.data.charter;
    this.viewType = this.data.type;
    this.showDuration = this.data.showDuration;
    if (this.viewType === 'all') {
      const payoutsSub = this.payoutDAO
        .createMockPayoutsForPricingDetails(data.rates, data.charter)
        .subscribe((mockPayouts) => {
          this.payouts = mockPayouts;
          this.filterPayouts();
          this.totalCost = this.getTotalCost();
        });
      this.subs.push(payoutsSub);
    } else {
      const payoutsSub = this.payoutDAO
        .createBOMockPayoutsForPricingDetails(data.rates, data.charter)
        .subscribe((mockPayouts) => {
          this.payouts = mockPayouts;
          this.filterPayouts();
          this.totalCost = this.getTotalCost();
        });
      this.subs.push(payoutsSub);
    }

    this.optionalAddOns = (this.charter.pricingMetaData?.addOns || [])
      .filter((addOn) => addOn && !addOn.required)
      .map((x) => ({ ...x, markupPrice: getAddOnCost(x, true) }));
    this.requiredAddOns = (this.charter.pricingMetaData?.addOns || [])
      .filter((addOn) => addOn && addOn.required)
      .map((x) => ({ ...x, markupPrice: getAddOnCost(x, true) }));
    this.optionalAddOnsTotalCost = this.optionalAddOns.reduce(
      (total, addOn) => total + addOn.price,
      0,
    );
    this.requiredAddOnsTotalCost = this.requiredAddOns.reduce(
      (total, addOn) => total + addOn.price,
      0,
    );
    this.optionalAddOnsTotalWithMarkUp = this.optionalAddOns.reduce(
      (total, addOn) => total + addOn.markupPrice,
      0,
    );
    this.requiredAddOnsTotalWithMarkUp = this.requiredAddOns.reduce(
      (total, addOn) => total + addOn.markupPrice,
      0,
    );
  }

  ngOnInit() {}

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  calculatePayoutWithMarkup(payout: Payout): number {
    let value = parseFloat(payout.basePay);
    if (payout.taxPercentage > 0) {
      value = parseFloat(payout.basePay) / (1 + payout.taxPercentage);
    }
    if (payout.markupPercentage > 0) {
      value += value * payout.markupPercentage;
    }
    return value;
  }

  getBuyflowTax(payouts: Payout[]): number {
    let tax = 0;
    payouts.forEach((payout) => {
      if (payout.taxPercentage > 0) {
        tax += this.calculatePayoutWithMarkup(payout) * payout.taxPercentage;
      }
    });
    return tax;
  }

  getBuyflowTotal(payouts: Payout[]): number {
    let total = 0;
    payouts.forEach((payout) => {
      if (this.viewType === 'buyflow' && payout.name === RateType.BookingFee) {
        return;
      }

      if (payout.taxPercentage > 0) {
        total +=
          this.calculatePayoutWithMarkup(payout) +
          this.calculatePayoutWithMarkup(payout) * payout.taxPercentage;
      } else {
        total += this.calculatePayoutWithMarkup(payout);
      }
    });
    if (['all', 'discount'].includes(this.viewType)) {
      total -= this.discountTotal;
      total += this.serviceTotal;

      if (this.promo) {
        total -= this.promo.promoDiscountAmount;
      }
    }

    return total - this.buyflowFilter();
  }

  getTotalCost(): number {
    let total = 0;
    const exclude = this.getExcludedPayoutNames();
    if (this.viewType === 'buyflow') {
      this.baseRatePayouts.forEach((payout) => {
        if (!exclude.includes(payout.name)) {
          total += parseFloat(payout.basePay) / (1 + payout.taxPercentage);
        }
      });
    } else {
      this.flattenedPayouts.forEach((payout) => {
        if (payout.name !== 'Booking Fee' && !exclude.includes(payout.name)) {
          total += parseFloat(payout.basePay) / (1 + payout.taxPercentage);
        }
      });
      total += this.optionalAddOnsTotalCost + this.requiredAddOnsTotalCost;
    }

    return total;
  }

  getPayoutPay(payout: any): number {
    if (!payout || !payout.basePay) {
      return 0;
    }
    return parseFloat(payout.basePay) / (1 + payout.taxPercentage);
  }

  getExcludedPayoutNames() {
    const exclude = [];
    if (this.viewType === 'buyflow') {
      exclude.push('Cleaning', DefinedAddOnType.EXTRA_GUEST_FEE);

      const affiliatePayoutIndex = this.payouts.findIndex(
        (x) => x.type === 'affiliate',
      );
      if (
        Number(this.charter.boat.owner.id) !==
        Number(this.payouts[affiliatePayoutIndex]?.userId)
      ) {
        exclude.push('Affiliate User');
      }
    }
    return exclude;
  }

  getAnchorNet(): number {
    return this.getBuyflowTotal(this.payouts) - this.totalCost;
  }

  getProfitPercentage(): number {
    return this.getAnchorNet() / this.getBuyflowTotal(this.payouts);
  }

  dismiss(): void {
    this.dialogRef.close();
  }

  private initDiscountsAndServices() {
    if (this.discounts) {
      this.discounts.forEach((discount) => {
        this.discountTotal += discount.amount;
      });
    }

    if (this.services) {
      this.services.forEach((service) => {
        this.serviceTotal += parseFloat(service.cost);
      });
    }
  }

  // todo - clean up this filtering for both listing payouts and totaling payouts
  buyflowFilter(): number {
    let value = 0;
    if (this.services) {
      this.services.forEach((service) => {
        this.payouts.forEach((payout) => {
          if (
            service.name === payout.name ||
            service.name + ' - Other' === payout.name
          ) {
            value += parseFloat(payout.basePay);
          }
        });
      });
      this.payouts.forEach((payout, index) => {
        if (payout.type === 'affiliate') {
          value += parseFloat(payout.basePay);
        }
      });
      return value;
    }
  }

  filterPayouts(): void {
    this.filteredPayouts = [...this.payouts];
    this.filterAffiliatePayout();

    if (this.data.services) {
      this.data.services.forEach((service) => {
        this.filteredPayouts.forEach((filterPayout, index) => {
          if (
            filterPayout.name === service.name ||
            filterPayout.name === service.name.trim() + ' - Other'
          ) {
            this.filteredPayouts.splice(index, 1);
          }
        });
      });
    }
    if (this.viewType === 'buyflow') {
      this.filteredPayouts = this.filteredPayouts.filter(
        (x) => x.name !== RateType.BookingFee,
      );
    }

    this.flattenedPayouts = [...this.payouts].reduce((acc, curr) => {
      if (curr.subPayouts?.length > 0) {
        curr.subPayouts.forEach((subPayout) => {
          acc.push({ name: curr.name, ...subPayout });
        });
      } else {
        acc.push(curr);
      }
      return acc;
    }, []);

    this.costPayouts = this.flattenedPayouts.filter((payout) => {
      return payout.name !== RateType.BookingFee;
    });

    this.baseRatePayouts = this.flattenedPayouts.filter((payout) => {
      return (
        ['owner', 'captain', 'crew'].includes(payout.type) ||
        payout.name === 'Fuel Included'
      );
    });

    this.filteredPayouts = this.filteredPayouts.filter(
      (x) => x.type !== 'addOn',
    );
  }

  private filterAffiliatePayout(): void {
    this.filteredPayouts.forEach((payout, index) => {
      if (payout.type === 'affiliate') {
        this.filteredPayouts.splice(index, 1);
      }
    });
  }

  canViewPayout(payout) {
    if (
      payout.name === 'Affiliate User' &&
      Number(this.charter.boat.owner.id) !== Number(payout.userId) &&
      this.viewType === 'buyflow'
    ) {
      return true;
    } else if (
      !['Booking Fee', 'Cleaning'].includes(payout.name) &&
      this.viewType === 'buyflow'
    ) {
      return true;
    } else if (this.viewType === 'all') {
      return true;
    }
    return false;
  }
}
